import idx from "idx";

import { MyCoachProductsQuery, ScheduleQuery } from "@/graphql";
import { ListAgentProfile } from "@/types/coaches";
import {
  InactiveProduct,
  Specialty,
  CategoryProduct,
  ListCoachProduct,
} from "@/types/products";
import { NonNullableArrayItem } from "@/utils/types";

type ScheduleCoach = NonNullableArrayItem<
  NonNullable<ScheduleQuery["currentUser"]>["coaches"]
>;

type MyCoachProduct = NonNullableArrayItem<
  MyCoachProductsQuery["activeCoachProducts"]
>;

type CoachProduct = (ListCoachProduct | MyCoachProduct) & {
  locked?: ListCoachProduct["locked"];
  enabled?: MyCoachProduct["enabled"];
  displayedCreditPrice?: string | null;
  acceptingNewClients?: boolean | null;
  introProduct?: boolean | null;
};

export const categoryTitle = {
  MEETING: "1:1 VIDEO SESSIONS",
  REVIEW: "OTHER SERVICES",
};

function coachProductReducer(
  result: Specialty[],
  coachProduct: CoachProduct,
): Specialty[] {
  const { product, acceptingNewClients, enabled } = coachProduct;
  const { specialty, category, introProduct } = product || {};

  if (!product) return [];

  // Create a new product object instead of modifying the existing one
  const enhancedProduct: CategoryProduct = {
    ...product,
    enabled: enabled,
    displayedCreditPrice: coachProduct.price || product.creditPrice,
    acceptingNewClients: acceptingNewClients,
    introProduct: introProduct || false,
  };

  const specialtiesIndex = result.findIndex(
    (element) => specialty?.id === element.id,
  );

  if (specialtiesIndex === -1 && specialty) {
    const specialtyObject: Specialty = {
      id: specialty.id,
      name: specialty.name,
      description: specialty.description,
      slug: specialty.slug,
      categories: [{ category: category, products: [enhancedProduct] }],
      inactiveProducts: [],
    };
    result.push(specialtyObject);
  } else {
    const specialtesItem = result[specialtiesIndex];
    const categoryIndex = specialtesItem?.categories?.findIndex(
      (element) => element.category === category,
    );
    if (categoryIndex === -1) {
      result[specialtiesIndex]?.categories?.push({
        category: category,
        products: [enhancedProduct],
      });
    } else {
      result[specialtiesIndex]?.categories?.[
        categoryIndex ?? 0
      ]?.products?.push(enhancedProduct);
    }
  }

  return result;
}

function specialtiesEnhancerReducer(
  enhancedSpecialties: Specialty[] = [],
  product: {
    id: string;
    name: string;
    specialty?: { id: string; name: string } | null;
  },
): Specialty[] {
  product = product || {
    specialty: { id: "0", name: "", description: "" },
  };

  const matchedSpecialty = enhancedSpecialties.find(
    (spec) => spec.id === product.specialty?.id,
  );

  if (!matchedSpecialty) {
    const newSpecialty: Specialty = {
      id: product.specialty?.id || "0",
      name: product.specialty?.name || "",
      categories: [],
      inactiveProducts: [product],
    };
    enhancedSpecialties.push(newSpecialty);
    return enhancedSpecialties;
  }

  if (!matchedSpecialty.inactiveProducts) {
    matchedSpecialty.inactiveProducts = [];
  }

  matchedSpecialty.inactiveProducts = [
    ...matchedSpecialty.inactiveProducts,
    product,
  ];

  return enhancedSpecialties;
}

interface CreateSpecialtiesProps {
  coachProducts: CoachProduct[];
  inactiveProducts?: InactiveProduct[];
  showIntroProduct?: boolean;
  onlyCategories?: string[];
}

export function createSpecialties({
  coachProducts = [],
  inactiveProducts = [],
  showIntroProduct = false,
  onlyCategories = [],
}: CreateSpecialtiesProps): Specialty[] {
  const coachProductsFiltered = coachProducts.filter((coachProduct) => {
    if (!coachProduct || !coachProduct.product || coachProduct.locked) {
      return false;
    }

    const { specialty, category, introProduct } = coachProduct.product;

    if (
      onlyCategories.length > 0 &&
      category &&
      !onlyCategories.includes(category)
    ) {
      return false;
    }

    return (
      coachProduct &&
      specialty &&
      category &&
      (showIntroProduct || !introProduct)
    );
  });

  const specialties = coachProductsFiltered.reduce(coachProductReducer, []);

  return inactiveProducts.reduce(specialtiesEnhancerReducer, [...specialties]);
}

export function checkCoachInUserCoaches({
  profile,
  coaches = [],
}: {
  profile?: ListAgentProfile | null;
  coaches?: ScheduleCoach[];
}): boolean {
  if (!profile || !coaches) return false;
  const coachId = idx(profile, (_) => _.user.id);
  if (!coachId) return false;
  return coaches.find((c) => c && c.id && c.id === coachId) !== undefined;
}

export function generateSpecialtiesSubstring(specialties: Specialty[]) {
  if (!specialties || !specialties.length) return "-";
  else if (specialties.length === 1) {
    return `- ${specialties[0].name} -`;
  } else if (specialties.length === 2) {
    return `- ${specialties[0].name} and ${specialties[1].name} -`;
  } else {
    return `- ${specialties[0].name}, ${specialties[1].name}, more -`;
  }
}

export function generateCoachSeoData(
  profile: ListAgentProfile,
  specialties: Specialty[],
) {
  const title = profile?.user?.name || "Coach Profile";
  const description = profile?.summary || "Coach profile";
  const specialtiesSubString = generateSpecialtiesSubstring(specialties);

  const coachData = {
    seoTitle: `${title} ${specialtiesSubString} ${
      IS_EXEC ? "Exec" : "Placement"
    }`,
    seoDescription: description,
  };

  return coachData;
}
